import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { first } from 'rxjs';
import { SeveridadeToast } from '../../models/enums/severidade-toast.enum';
import { AuthService } from '../../services/auth.service';
import { ToastService } from '../../services/toast.service';
import { CountryService } from '../../services/country-service.service';
import { selectIdentificacaoCpfCnpj } from '../../models/enums/identificacao-cpf-cnpj.enum';
import { IdentificacaoCpfCnpj } from '../../models/enums/identificacao-cpf-cnpj.enum';
import { LocationService } from '../../services/location-service.service';
import { SpinnerService } from '../../services/spinner.service';
import { provideNgxMask } from 'ngx-mask';
import { RegimeApuracaoSimplesNacional } from '../../models/enums/regimeApuracaoSimplesNacional.enum';
import { FormatadorHelper } from '../../helpers/formatador.helper';
import { getErrorMessage } from '../../helpers/error-message.helper';
import {
  CERTIFICADO_ACCEPT,
  CERTIFICADO_TAMANHO_MAX_BYTES,
  validarArquivoCertificado
} from '../../helpers/certificado-digital.helper';
import { FileUpload } from 'primeng/fileupload';

enum RegimeTributarioEnum {
  SIMPLES_NACIONAL = 1
}

interface Estado {
  label: string;
  value: string;
  id: string;
}

@Component({
  selector: 'app-sign-up',
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.scss'],
  providers: [provideNgxMask()]
})
export class SignUpComponent implements OnInit {
  router = inject(Router);
  authService = inject(AuthService);
  route = inject(ActivatedRoute);
  toastService = inject(ToastService);
  countryService = inject(CountryService);
  locationService = inject(LocationService);
  spinnerService = inject(SpinnerService);

  form: FormGroup = new FormGroup({});
  estados: Estado[] = [];
  municipios: { label: string; value: string }[] = [];
  regimeApuracaoOptions: { label: string; value: number }[] = [];

  isAddMode = true;
  submitted = false;
  paises: { label: string, value: string }[] = [];
  regimesTributarios = [
    { label: 'Simples Nacional', value: RegimeTributarioEnum.SIMPLES_NACIONAL }
  ];
  tipoDocumentoLabel: string = 'CNPJ';

  fileName: string | null = null;
  identificacaoCpfCnps: string = IdentificacaoCpfCnpj.Cnpj;
  identificacoesCpfCnpjs: any = [];
  certificadoBaseList: File[] = [];
  certificadoInvalid: boolean = false;
  certificadoFormatoInvalid: boolean = false;
  readonly acceptCertificado = CERTIFICADO_ACCEPT;
  readonly maxCertificadoBytes = CERTIFICADO_TAMANHO_MAX_BYTES;
  isCpfSelected: boolean = false;
  readonly mascaraCnpj = FormatadorHelper.MASCARA_CNPJ;
  readonly exemploCnpj = FormatadorHelper.EXEMPLO_CNPJ;

  @ViewChild('certificadoUpload') certificadoUpload?: FileUpload;

  ngOnInit(): void {
    this.initializeRegimeApuracaoOptions();

    this.form = new FormGroup({
      nomeEmpresa: new FormControl('', Validators.required),
      nomeFantasia: new FormControl('', Validators.required),
      email: new FormControl('', [Validators.required, Validators.email]),
      senha: new FormControl('', [
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(50),
        Validators.pattern('^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[!@#$%^&*(),.?":{}|<>]).+$')
      ]),
      senhaConfirmacao: new FormControl('', Validators.required),
      tipoIdentificacaoCpfCnpj: new FormControl(IdentificacaoCpfCnpj.Cnpj, Validators.required),
  
      cpfCnpj: new FormControl('', [Validators.required, Validators.maxLength(18)]),
      cnpjEmpresa: new FormControl('', [Validators.maxLength(18)]),
      logradouro: new FormControl('', [Validators.required, Validators.maxLength(255)]),
      numero: new FormControl('', [Validators.required, Validators.maxLength(10)]),
      complemento: new FormControl('', [Validators.required, Validators.maxLength(50)]),
      bairro: new FormControl('', [Validators.required, Validators.maxLength(255)]),
      municipio: new FormControl('', Validators.required),
      codigoMunicipio: new FormControl('', Validators.required),
      uf: new FormControl('', [Validators.required, Validators.minLength(2), Validators.maxLength(2)]),
      cep: new FormControl('', [Validators.required, Validators.minLength(8), Validators.maxLength(15)]),
      pais: new FormControl({ value: 'BR', disabled: true }, [Validators.maxLength(100)]),
      telefone: new FormControl('', [Validators.required, Validators.pattern('^[0-9]{10,12}$')]),
      oab: new FormControl('', Validators.maxLength(20)),
      inscricaoMunicipal: new FormControl('', [Validators.required, Validators.maxLength(20)]),
      codigoTributarioMunicipio: new FormControl('', [Validators.required]),
      regimeTributario: new FormControl({ value: RegimeTributarioEnum.SIMPLES_NACIONAL, disabled: true }),
      RegimeApuracaoSimplesNacional: new FormControl(RegimeApuracaoSimplesNacional.Caixa), 
      arquivoCertificadoBase64: new FormControl('', Validators.required),
      senhaCertificado: new FormControl('', Validators.required)
    }, { validators: this.passwordsMatchValidator });
  
    this.paises = this.countryService.getCountries();
    this.form.get('pais')?.setValue('BR');
    this.identificacoesCpfCnpjs = selectIdentificacaoCpfCnpj;
    
    this.onChangeidentificacaoCpfCnps(IdentificacaoCpfCnpj.Cnpj);
  
    this.locationService.getEstados().subscribe(estados => {
      this.estados = estados
        .map((estado: any) => ({
          label: estado.nome,
          value: estado.sigla,
          id: estado.id
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    });
  
    this.form.get('uf')?.valueChanges.subscribe(ufSigla => {
      if (ufSigla) {
        const estado = this.estados.find(e => e.value === ufSigla);
        if (estado) {
          this.locationService.getMunicipiosPorUf(estado.id).subscribe(municipios => {
            this.municipios = municipios
              .map((municipio: any) => ({
                label: municipio.nome,
                value: municipio.id
              }))
              .sort((a, b) => a.label.localeCompare(b.label));
            this.form.get('municipio')?.setValue('');
            this.form.get('codigoMunicipio')?.setValue('');
          });
        }
      }
    });
  
    this.form.get('municipio')?.valueChanges.subscribe(municipioId => {
      if (municipioId && this.municipios.length > 0) {
        const municipio = this.municipios.find(m => m.value === municipioId);
        if (municipio) {
          this.form.get('codigoMunicipio')?.setValue(municipio.value);
        }
      }
    });
  

  }
  
  regimeTributarioEnum = RegimeTributarioEnum;
  get f() { return this.form.controls; }



  onSubmit() {
    this.submitted = true;

    this.form.get('regimeTributario')?.enable();
    this.form.get('pais')?.enable();

    this.form.patchValue({ regimeTributario: RegimeTributarioEnum.SIMPLES_NACIONAL });

    if (this.form.invalid) {
      this.certificadoInvalid = this.form.get('arquivoCertificadoBase64')?.hasError('required') ?? false;
      this.form.get('pais')?.disable();
      return;
    }

    const erroCertificado = validarArquivoCertificado(this.certificadoBaseList[0]);
    if (erroCertificado) {
      this.certificadoInvalid = true;
      this.certificadoFormatoInvalid = true;
      this.toastService.setMensagemToast(erroCertificado, SeveridadeToast.Error);
      this.form.get('pais')?.disable();
      return;
    }

    if (this.isAddMode) {
      this.createUser();
    }

    this.form.get('pais')?.disable();
  }

  onFileChange(event: any): void {
    const files = event?.files as File[] | undefined;
    const file = files && files.length > 0 ? files[0] : null;

    if (!file) {
      this.limparSelecaoCertificado();
      return;
    }

    const erro = validarArquivoCertificado(file);
    if (erro) {
      this.certificadoFormatoInvalid = true;
      this.certificadoInvalid = false;
      this.limparSelecaoCertificado(false);
      this.toastService.setMensagemToast(erro, SeveridadeToast.Error);
      this.certificadoUpload?.clear();
      return;
    }

    this.certificadoBaseList = [file];
    this.fileName = file.name;
    this.certificadoInvalid = false;
    this.certificadoFormatoInvalid = false;
    this.form.get('arquivoCertificadoBase64')?.setValue(this.fileName);
  }

  private limparSelecaoCertificado(marcarInvalidoRequired = true): void {
    this.certificadoBaseList = [];
    this.fileName = null;
    this.form.get('arquivoCertificadoBase64')?.setValue('');
    if (marcarInvalidoRequired) {
      this.certificadoInvalid = true;
    }
  }


  getMask(): string {
    if (this.identificacaoCpfCnps === IdentificacaoCpfCnpj.Cpf) {
      return FormatadorHelper.MASCARA_CPF;
    } else if (this.identificacaoCpfCnps === IdentificacaoCpfCnpj.Exterior) {
      return '';
    }

    return FormatadorHelper.MASCARA_CNPJ;
  }

  onDocumentoInput(event: Event, controlName: 'cpfCnpj' | 'cnpjEmpresa'): void {
    const input = event.target as HTMLInputElement;
    const isExterior = controlName === 'cpfCnpj' && this.identificacaoCpfCnps === IdentificacaoCpfCnpj.Exterior;

    if (isExterior) {
      return;
    }

    const isCpf = controlName === 'cnpjEmpresa'
      ? false
      : this.identificacaoCpfCnps === IdentificacaoCpfCnpj.Cpf;

    this.form.get(controlName)?.setValue(
      FormatadorHelper.aplicarMascaraDuranteInput(input.value, isCpf),
      { emitEvent: false }
    );
  }

  getPhoneMask(): string {
    return this.form.get('telefone')?.value?.length === 11 ? '00 000000000' : '00 00000000';
  }

  onChangeidentificacaoCpfCnps(value: string) {
    this.identificacaoCpfCnps = value;
    this.isCpfSelected = value === IdentificacaoCpfCnpj.Cpf;
    const isExterior = value === IdentificacaoCpfCnpj.Exterior;

    this.form.get('cpfCnpj')?.setValue('');

    if (this.isCpfSelected) {
      this.tipoDocumentoLabel = 'CPF';
    } else if (isExterior) {
      this.tipoDocumentoLabel = 'Documento Exterior';
    } else {
      this.tipoDocumentoLabel = 'CNPJ';
    }

    const cnpjEmpresaControl = this.form.get('cnpjEmpresa');
    const cpfCnpjControl = this.form.get('cpfCnpj');

    if (this.isCpfSelected) {
      cnpjEmpresaControl?.setValidators([Validators.maxLength(18)]);
      cpfCnpjControl?.setValidators([Validators.required, Validators.maxLength(18)]);
    } else if (isExterior) {
      cnpjEmpresaControl?.clearValidators();
      cpfCnpjControl?.clearValidators();
    } else {
      cnpjEmpresaControl?.clearValidators();
      cpfCnpjControl?.setValidators([Validators.required, Validators.maxLength(18)]);
    }

    cnpjEmpresaControl?.updateValueAndValidity();
    cpfCnpjControl?.updateValueAndValidity();
  }

  private createUser() {
    this.spinnerService.show('Criando conta...');
    const formData = new FormData();

    Object.keys(this.form.controls).forEach(key => {
      const controlValue = this.form.get(key)?.value;

      if (controlValue !== null && controlValue !== undefined) {
        if (key !== 'arquivoCertificadoBase64' && key !== 'tipoIdentificacaoCpfCnpj') {
          if (key === 'codigoMunicipio') {
            const codigoMunicipioValue = controlValue.toString().slice(0, 7);
            formData.append(key, codigoMunicipioValue);
          } else if (key === 'municipio') {
            const municipio = this.municipios.find(m => m.value === controlValue);
            if (municipio) {
              formData.append(key, municipio.label);
            } else {
              formData.append(key, controlValue);
            }
          } else if (key === 'cpfCnpj' || key === 'cnpjEmpresa') {
            const documentoNormalizado = FormatadorHelper.normalizarDocumento(controlValue);
            if (documentoNormalizado) {
              formData.append(key, documentoNormalizado);
            }
          } else {
            formData.append(key, controlValue);
          }
        }
      }
    });

    formData.append('tipoIdentificacaoCpfCnpj', this.identificacaoCpfCnps);

    if (this.certificadoBaseList && this.certificadoBaseList.length > 0) {
      this.certificadoBaseList.forEach(file => {
        formData.append('arquivoCertificadoBase64', file, file.name);
      });
    }

    this.authService.usuarioCadastro(formData)
      .pipe(first())
      .subscribe({
        next: () => {
          this.retornarMensagemSucesso('Conta criada com sucesso!');
          this.router.navigate(['../'], { relativeTo: this.route });
          this.spinnerService.hide();
        },
        error: (e) => {
          this.retornarMensagemErro(e);
          console.error('Erro ao criar conta:', e);
          this.spinnerService.hide();
        }
      });
  }

  private retornarMensagemSucesso(mensagem: any) {
    let successMessage: string;

    if (typeof mensagem === 'string') {
      successMessage = mensagem;
    } else if (mensagem && mensagem.message) {
      successMessage = mensagem.message;
    } else {
      successMessage = 'Operação realizada com sucesso!';
    }

    this.toastService.setMensagemToast(successMessage, SeveridadeToast.Success);
  }


  private retornarMensagemErro(e: any) {
    const errorMessage = getErrorMessage(
      e,
      'Não foi possível criar a conta. Verifique os dados informados e tente novamente.'
    );

    this.toastService.setMensagemToast(`Erro ao criar conta: ${errorMessage}`, SeveridadeToast.Error);
  }


  private passwordsMatchValidator(control: AbstractControl): ValidationErrors | null {
    const senha = control.get('senha')?.value;
    const senhaConfirmacao = control.get('senhaConfirmacao')?.value;
    return senha === senhaConfirmacao ? null : { passwordsDoNotMatch: true };
  }

  private initializeRegimeApuracaoOptions(): void {
    this.regimeApuracaoOptions = [
      { label: 'Caixa', value: RegimeApuracaoSimplesNacional.Caixa },
      { label: 'Competência', value: RegimeApuracaoSimplesNacional.Competencia }
    ];
  }

  navigateToLogin(): void {
    this.router.navigate(['/login']);
  }
}
